import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function parse(s) {
  return s ? new Date(`${s}T00:00:00`) : null;
}

// Self-contained month calendar for the registration Start-date step. Blocks
// today/past (via `min`), anything past `max`, and the configured non-start
// weekdays (default Saturday + Sunday) — those days render greyed out and are
// not clickable, so the customer can only pick a valid start weekday.
export function RegisterCalendar({ value, min, max, onChange, blockedDays = [0, 6] }) {
  const anchor = parse(value) || parse(min) || new Date();
  const [view, setView] = useState({ year: anchor.getFullYear(), month: anchor.getMonth() });

  const first = new Date(view.year, view.month, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(view.year, view.month, d));

  const monthKey = `${view.year}-${String(view.month + 1).padStart(2, "0")}`;
  const canPrev = monthKey > (min ? min.slice(0, 7) : "0000-00");
  const canNext = monthKey < (max ? max.slice(0, 7) : "9999-99");

  const go = (delta) => {
    const nd = new Date(view.year, view.month + delta, 1);
    setView({ year: nd.getFullYear(), month: nd.getMonth() });
  };

  const isDisabled = (d) => {
    const s = ymd(d);
    if (min && s < min) return true;
    if (max && s > max) return true;
    return blockedDays.includes(d.getDay());
  };

  return (
    <div className="rounded-xl border border-border bg-surface p-3" data-testid="register-calendar">
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button" onClick={() => go(-1)} disabled={!canPrev}
          className="flex h-8 w-8 items-center justify-center rounded-full text-text-main transition-colors hover:bg-background disabled:opacity-30"
          data-testid="register-calendar-prev" aria-label="Previous month"
        >
          <ChevronLeft size={18} strokeWidth={1.75} />
        </button>
        <p className="text-sm font-semibold text-text-main" data-testid="register-calendar-month">
          {MONTHS[view.month]} {view.year}
        </p>
        <button
          type="button" onClick={() => go(1)} disabled={!canNext}
          className="flex h-8 w-8 items-center justify-center rounded-full text-text-main transition-colors hover:bg-background disabled:opacity-30"
          data-testid="register-calendar-next" aria-label="Next month"
        >
          <ChevronRight size={18} strokeWidth={1.75} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS.map((w, i) => (
          <div key={w} className={`py-1 text-[11px] font-medium ${blockedDays.includes(i) ? "text-text-muted/40" : "text-text-muted"}`}>
            {w}
          </div>
        ))}
        {cells.map((d, i) => {
          if (!d) return <div key={`blank-${i}`} />;
          const s = ymd(d);
          const disabled = isDisabled(d);
          const selected = value === s;
          return (
            <button
              key={s}
              type="button"
              disabled={disabled}
              onClick={() => onChange(s)}
              data-testid={`register-calendar-day-${s}`}
              className={`flex h-9 items-center justify-center rounded-lg text-sm transition-colors ${
                selected
                  ? "bg-primary font-semibold text-white"
                  : disabled
                    ? "cursor-not-allowed text-text-muted/30"
                    : "text-text-main hover:bg-primary/10"
              }`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
